/**
 * PublicDemoBadge — persistent "DEMO MODE" pill anchored top-center
 * with a Reset button. Renders only when the app is running in public
 * demo mode (`demo.trubeaute.com`, `REACT_APP_DEMO_MODE=true`, or
 * `?demo=1`).
 *
 * Positioned as a fixed overlay above the app content so it never
 * scrolls out of view. High z-index so it sits above any modal.
 * Keyboard-accessible (button element, standard focus ring).
 */
import React, { useState } from "react";
import { Sparkles, RotateCcw } from "lucide-react";
import { usePublicDemo } from "../context/PublicDemoContext";

const PublicDemoBadge = () => {
  const { isDemoMode, resetDemo } = usePublicDemo();
  const [confirming, setConfirming] = useState(false);

  if (!isDemoMode) return null;

  const onClickReset = () => {
    if (!confirming) {
      setConfirming(true);
      // Auto-cancel confirmation after 4s so the button doesn't stay
      // in the "danger" state forever.
      setTimeout(() => setConfirming(false), 4000);
      return;
    }
    resetDemo();
  };

  return (
    <div
      className="fixed top-3 left-3 sm:top-4 sm:left-4 z-[200] flex items-center gap-2 pointer-events-none"
      data-testid="public-demo-badge-root"
      aria-live="polite"
    >
      {/* Badge pill */}
      <div
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-[#2C2123]/90 text-white px-3 py-1.5 text-[11px] tracking-wider uppercase shadow-lg backdrop-blur-sm"
        data-testid="public-demo-badge-pill"
      >
        <Sparkles className="h-3.5 w-3.5 text-[#F2B7B0]" aria-hidden="true" />
        <span className="font-medium">Demo Mode</span>
      </div>

      {/* Reset button (two-step confirmation) */}
      <button
        type="button"
        onClick={onClickReset}
        className={`pointer-events-auto inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-wider transition-all backdrop-blur-sm shadow-md ${
          confirming
            ? "bg-[#9B6A6C] text-white border-[#9B6A6C] hover:bg-[#7d5455]"
            : "bg-white/90 text-[#2C2123] border-[#EADDD7] hover:bg-white"
        }`}
        data-testid="public-demo-reset-btn"
        aria-label={confirming ? "Confirm reset demo" : "Reset demo"}
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        {confirming ? "Tap again to reset" : "Reset Demo"}
      </button>
    </div>
  );
};

export default PublicDemoBadge;
